import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { isNotNil } from '@flowio/is';
import { createConsumer } from '@flowio/postmessage';
import { getOwnerDocument, getOwnerWindow, getScrollbarWidth, getStyle, isScrollbarVisible } from '@flowio/browser-helpers';
import { TaskQueue } from '@flowio/task-queue';

/**
 * Use `isLocalHost` to check for localhost domain.
 * @returns True if on localhost domain.
 */
export function isLocalhost() {
  return Boolean(window.location.hostname === 'localhost' // [::1] is the IPv6 localhost address.
  || window.location.hostname === '[::1]' // 127.0.0.0/8 are considered localhost for IPv4.
  || window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/));
}
var consumer;
var container;
var hasCheckoutStarted = false;
var bodyOverflow;
var bodyPaddingRight;
var urlRegexTest = /^https?:\/\/.+/;
var queue = new TaskQueue();
var blazeUrls = {
  integration: 'https://integration-pay.flow.io',
  production: 'https://pay.flow.io'
};
var consumerConfig = {
  blazeUrl: blazeUrls.production
};

function parseInt(string, defaultValue) {
  var value = Number.parseInt(string, 10);
  return Number.isNaN(value) ? defaultValue : value;
}

function enableScrolling() {
  var document = getOwnerDocument(container);

  if (isNotNil(bodyOverflow)) {
    document.body.style.overflow = bodyOverflow;
  }

  if (isNotNil(bodyPaddingRight)) {
    document.body.style.paddingRight = bodyPaddingRight;
  }
}

function disableScrolling() {
  var window = getOwnerWindow(container);
  var document = getOwnerDocument(container);

  if (isNotNil(window) && isScrollbarVisible(window)) {
    bodyPaddingRight = getStyle(document.body, 'paddingRight') || '0';
    var scrollbarWidth = getScrollbarWidth();
    var paddingRight = parseInt(bodyPaddingRight, 0);
    document.body.style.paddingRight = "".concat(scrollbarWidth + paddingRight, "px");
  }

  bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
}

function exitCheckout() {
  enableScrolling();
  container.style.position = 'static';
  container.style.height = '0';
  hasCheckoutStarted = false;
}
/**
 * An object that configures your integration.
 */


/**
 * Use `startCheckout` to render Blaze Checkout on the browser.
 * @returns void
 */
export function startCheckout() {
  disableScrolling();
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.right = '0';
  container.style.bottom = '0';
  container.style.left = '0';
  container.style.height = '100%'; // @ts-ignore

  container.style.backdropFilter = 'blur(10px)';
  container.style.backgroundColor = 'rgba(70,77,86,0.25)';
  container.style.opacity = '1';
  container.style.setProperty('z-index', '2147483647', 'important');
  consumer.postMessage({
    discriminator: 'start_checkout'
  });
  hasCheckoutStarted = true;
}

function initCheckout(sessionId, customerToken) {
  consumer.postMessage({
    discriminator: 'initialize',
    session_id: sessionId,
    customer_token: customerToken
  });

  if (hasCheckoutStarted) {
    // RESET BG WHEN GOING BACK TO CHECKOUT
    if (container) {
      container.style.background = 'rgba(70,77,86,0.25)';
    }

    consumer.postMessage({
      discriminator: 'start_checkout'
    });
  }
}

function getRemote() {
  return consumerConfig.blazeUrl;
}
/**
 * Use `initialize` to configure and mount Blaze Checkout on your website.
 */


export function initialize(_ref) {
  var sessionId = _ref.sessionId,
      customerToken = _ref.customerToken;
  container = document.createElement('div');
  container.style.height = '0';
  document.body.appendChild(container);
  var remote = getRemote();
  consumer = createConsumer({
    remote: remote,
    container: container,
    style: {
      height: '100%',
      width: '100%',
      display: 'block'
    }
  });
  consumer.subscribe(function (message) {
    switch (message.discriminator) {
      case 'syn_initialize':
        initCheckout(sessionId, customerToken);
        break;

      case 'payment_auth_start':
        // SET BG TO WHITE FOR NEUTRAL PAYMENT WINDOW DESIGN
        if (container && container.style.background !== 'white') {
          container.style.background = 'white';
        }

        break;

      case 'exit_checkout_confirmation':
      case 'exit_checkout':
        exitCheckout();
        break;

      default:
        break;
    }
  });
  queue.run();
}

var withQueue = function withQueue(callback) {
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    queue.insert(function () {
      callback.apply(void 0, args);
    });
  };
};

/**
 * Use `addOrderAttributes` to insert attributes into the order.
 * @param t An object representing the order attributes to be added.
 * @returns void
 */
export var addOrderAttributes = withQueue(function (attributes) {
  consumer.postMessage({
    discriminator: 'add_order_attributes',
    attributes: attributes
  });
});
/**
 * Use `addItemToCart` to notify that your customer would like to purchase an item.
 * This operation will update the internal order state to include the specified item.
 * This operation can also be useful to build the order state incrementally instead of
 * waiting until your customers click on a checkout button to define the line items.
 * @param t Item object to add to cart. See [[BlazeCheckoutItem]].
 * @returns void
 */

export var addItemToCart = withQueue(function (item) {
  consumer.postMessage({
    discriminator: 'add_to_cart',
    item: item
  });
});
/**
 * Use `setCartItems` to define the order line items.
 * This operation will replace existing line items in the order state.
 * @param t Array of items to set.
 * @returns void
 */

export var setCartItems = withQueue(function (items) {
  consumer.postMessage({
    discriminator: 'set_cart_items',
    items: items
  });
});
/**
 * @hidden
 * Need description
 * @param t Array of items to update.
 * @returns void
 */

export var updateCartItems = withQueue(function (items) {
  consumer.postMessage({
    discriminator: 'update_cart_items',
    items: items
  });
});

/**
 * @hidden
 * Needs description
 * @param t
 * @returns void
 */
export var setApplePayAvailability = withQueue(function (response) {
  consumer.postMessage(_objectSpread({
    discriminator: 'applepay_availability_response'
  }, response));
});
/**
 * @hidden
 * Needs description
 * @param t Promotion code to apply.
 * @returns void
 */

export var setPromotionCode = withQueue(function (promotion_code) {
  consumer.postMessage({
    discriminator: 'set_promotion_code',
    promotion_code: promotion_code
  });
});
/**
 * @hidden
 * Needs description
 * @param subscriber
 * @returns void
 */

export var subscribe = function subscribe(subscriber) {
  if (!consumer) {
    throw new Error('subscribe: consumer not initialized.');
  }

  return consumer.subscribe(subscriber);
};
/**
 * @hidden
 * Needs description
 * @param options
 * @returns void
 */

export function config(options) {
  if (options.blazeUrl) {
    if (!urlRegexTest.test(options.blazeUrl)) {
      return;
    }

    if (!(isLocalhost() || Object.values(blazeUrls).includes(options.blazeUrl))) {
      return;
    }
  }

  consumerConfig = _objectSpread(_objectSpread({}, consumerConfig), options);
}


//////////////////
// WEBPACK FOOTER
// ./~/@flowio/blaze-consumer/lib/esm/index.js
// module id = 461
// module chunks = 0 1